/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Row, Col } from "react-awesome-styled-grid";
import { ModalConfirm, Spinner } from "@nordcloud/gnui";
import { useAuth0 } from "~/components/Auth/auth";
import { CommentContent } from "~/components/Comment/CommentContent";
import { CommentForm } from "~/components/Comment/CommentForm";
import { CommentMetadata } from "~/components/Comment/CommentMetadata";
import { CommentModifier } from "~/components/Comment/CommentModifier";
import { Action } from "~/constants";
import { useUserConsumer } from "~/context/User/User";
import { Comment } from "~/models";
import { isPrivilegedUser } from "~/routing";
import { useClickOutside } from "~/tools";

type InlineCommentsProps = {
  item: Comment;
  property: "text";
  onSubmit: (values: Comment, type: string) => Promise<void>;
};

export type Val = {
  text: string;
};

function InlineComments({ item, property, onSubmit }: InlineCommentsProps) {
  const [isEditMode, setIsEditMode] = React.useState<boolean>(false);
  const [isRemoveMode, setIsRemoveMode] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [height, setHeight] = React.useState(0);

  const onRemove = async () => {
    setIsRemoveMode(false);
    setIsLoading(true);
    await onSubmit(item as Comment, Action.REMOVE);
  };

  const onEdit = async (values: Val) => {
    setIsEditMode(false);
    setIsLoading(true);
    const organizeValues = {
      ...item,
      ...values,
    };

    await onSubmit(organizeValues as Comment, Action.EDIT);
  };

  const wrapperRef = React.useRef(null);

  useClickOutside(
    wrapperRef,
    () => {
      setIsRemoveMode(false);
    },
    isRemoveMode
  );
  const { user } = useAuth0();
  const { role } = useUserConsumer();

  function RemoveModal() {
    return (
      <ModalConfirm
        contentLabel="Delete comment"
        isOpen={isRemoveMode}
        confirm={onRemove}
        onClose={() => setIsRemoveMode(false)}
        actionLabel="Delete"
      >
        Do you really want to delete this comment?
      </ModalConfirm>
    );
  }

  const main = () => {
    if (isLoading) {
      return <Spinner size="lg" />;
    }
    if (isEditMode) {
      return (
        <CommentForm
          property={property}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          onSubmit={onEdit}
          item={item}
          height={height}
          newComment={false}
        />
      );
    }

    return (
      <>
        <CommentContent content={item[property] || ""} setHeight={setHeight} />
        <RemoveModal />
      </>
    );
  };

  return (
    <Row
      align="flex-start"
      justify="center"
      css={{ marginBottom: "2.5rem" }}
      data-cy="inline-comment"
    >
      <Col xs={1} sm={2} md={2} lg={2}>
        <CommentMetadata
          author={item.log.created.author}
          time={item.log.created.time || ""}
          modified={item.log.modified}
        />
      </Col>
      <Col xs={2} sm={4} md={5} lg={6}>
        {main()}
      </Col>
      <Col xs={1} sm={1} md={1} lg={1}>
        {!isEditMode &&
          (user?.email === item.log.created.author ||
            isPrivilegedUser(role)) && (
            <CommentModifier
              author={user?.email === item.log.created.author}
              setIsEditMode={setIsEditMode}
              setIsRemoveMode={setIsRemoveMode}
            />
          )}
      </Col>
    </Row>
  );
}

export { InlineComments };
